import type { JSX } from "react";

import { SharingModalOption } from "app/components/SharingModal/components/SharingModalOption/SharingModalOption";
import { RestrictedOptionList } from "app/components/SharingModal/components/RestrictedOptionList/RestrictedOptionList";
import type { ISharingModalState, ISharingModalOptionTextBase } from "app/components/SharingModal/types";
import { Container } from "./OptionContainer.styled";
import { getUpdatedSharingState } from "app/components/SharingModal/utils";
import type { IUserInfoExtended } from "app/components/SharingModal/types";

interface IOptionContainerProps<T> extends ISharingModalState {
    sharing: ISharingModalState;
    textContainer: ISharingModalOptionTextBase;
    sharedWithUsers: Array<number>;
    onShare: (sharingState: ISharingModalState) => void;
    users: T[];
    hasSharedUsersList?: boolean;
}

export const OptionContainer = <T extends IUserInfoExtended>({
    sharing,
    sharingState,
    textContainer,
    sharedWithUsers,
    isOptionDisabled,
    onShare,
    sharedWithUsersToShow,
    sharedWithInvitations,
    users,
    hasSharedUsersList = false,
}: IOptionContainerProps<T>): JSX.Element => {
    const { title, subtitle, iconName = "users" } = textContainer;

    const handleRemoveUser = ({ isInvite, id }: T): void => {
        const sharingUpdated = getUpdatedSharingState(
            sharedWithUsers,
            sharedWithInvitations,
            id,
            sharedWithUsersToShow,
            sharing,
            isInvite,
        );

        onShare(sharingUpdated);
    };

    const handleSelectOption = (): void => {
        if (sharingState === textContainer.optionIndex) {
            return;
        }

        const sharingUpdated = { ...sharing, sharingState: textContainer.optionIndex };
        onShare(sharingUpdated);
    };

    const isSelected = sharingState === textContainer.optionIndex;

    return (
        <>
            <SharingModalOption
                onClick={handleSelectOption}
                isSelected={isSelected}
                iconName={iconName}
                title={title}
                subTitle={subtitle}
                isDisabled={isOptionDisabled}
            />
            {hasSharedUsersList ? (
                <Container isVisible={isSelected}>
                    <RestrictedOptionList
                        onRemoveUser={handleRemoveUser}
                        selectedUsersIDs={sharedWithUsersToShow}
                        users={users}
                    />
                </Container>
            ) : null}
        </>
    );
};
